import consola from 'consola'
import type { Subscription } from 'rxjs'
import { createAnnualGrowthPosEntry } from '~/composables/annual-growth'
import type { MultiGenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import { type InputManualMap, MethodologyEnum, PlanItemLogicEnum, type PlanPosItem, type StcsCalcItemStatus, type StcsCompPlanCfgItem } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import { StcsDisplayValueTypeEnum, StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgFsdFlatItem } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { CompPlanCfgCalcRequest } from '~/protos/gen/stcs_wapp_svc_comp_plan'
import { cmwCompPlanCfgCalcRequest } from '~/services/cmw.service'
import { cleanString, findRowByEndsWith } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store'
import { createPlanningViewStore } from '~/stores/planningview.store'
import type { FormulaToken } from '~/types/formula-based'
import { createFormulaBasedPosEntry } from './formula-based'
import { createMonthlyGrowthPosEntry } from './monthly-growth'

// Define regex patterns at module level for performance
const TIMESTAMP_SUFFIX_PATTERN = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CTX_LINE_SUFFIX = '_ctx_line'
const LEVEL_1_PATTERN = /level_1_([a-z_]+)(?=_level_2|$)/
const LEVEL_2_PATTERN = /level_2_([a-z_0-9-]+)$/

/**
 * Extract level_1 ID from row_id using the pattern from actual data
 * Example: bs_total_total_liabilities_n_equity_sub_total_equity_n_reserves_level_1_equity_level_2_retained_earnings
 * Returns: "level_1_equity"
 */
export function extractLevel1Id(rowId: string): string {
  const match = rowId.match(LEVEL_1_PATTERN)
  if (match?.[1]) {
    return `level_1_${match[1]}`
  }
  return ''
}

/**
 * Extract level_2 ID from row_id using the pattern from actual data
 * Example 1: bs_total_total_liabilities_n_equity_sub_total_equity_n_reserves_level_1_equity_level_2_retained_earnings
 * Returns: "level_2_retained_earnings"
 * Example 2: bs_total_assets_sub_total_current_assets_n_accruals_level_1_trade_receivables_level_2_01966bcd-d1d5-796f-a5dd-b8d9fd6842d1
 * Returns: "level_2_01966bcd-d1d5-796f-a5dd-b8d9fd6842d1"
 */
export function extractLevel2Id(rowId: string): string {
  const match = rowId.match(LEVEL_2_PATTERN)
  if (match?.[1]) {
    return `level_2_${match[1]}`
  }
  return ''
}

/**
 * Understanding the Edit API Payload Structure:
 *
 * 1. FSD Rows (Main Rows): Come from the fsd service, represent the base financial statement structure
 * 2. Custom Rows: Added by users with rowClass like 'custom-planning-row', 'custom-row', 'sub-custom-row'
 * 3. Planning Methods: Applied to custom rows (selectedMethod, values, etc.)
 * 4. Storage Strategy: Since FSD rows can't be edited, custom rows are stored in pos_map under parent row_id
 *
 * Payload Structure:
 * - fstmt_id: Original FSD row ID
 * - pos_map: Contains all custom planning rows under their parent FSD row
 *   - Each custom row has: selectedMethod, values_map, i18n_map, etc.
 *   - Custom rows are identified by rowClass properties
 *
 * New PlanPosItem Attributes (commit: 4d9a13c75c94b33e295e83a3e14a1a4ea4e270e8):
 * - pos_index: number - Used to maintain proper ordering of positions as they come back from backend
 *   (fixes issue where backend returns positions in different order than sent)
 * - is_struct_item: boolean - Set to true only for 3 specific BS structural rows:
 *   * bs_total_total_liabilities_n_equity_sub_total_equity_n_reserves_level_1_equity_level_2_retained_earnings
 *   * bs_total_total_liabilities_n_equity_sub_total_equity_n_reserves_level_1_equity_level_2_profit_loss_carryforward
 *   * bs_total_assets_sub_total_current_assets_n_accruals_level_1_cash_n_cash_equivalents_level_2_cash_n_cash_equivalents
 */

// Extend StcsOrgFsdFlatItem with planning context for type safety
export type PlanningRowData = StcsOrgFsdFlatItem & {
  ctx_reference_row_id?: string
  selectedMethod?: MethodologyEnum
  outputType?: StcsDisplayValueTypeEnum
  formulaTokens?: FormulaToken[] // Store the parsed formula tokens for reconstructing the formula editor state
  referenceRowId?: string
  is_position_row?: boolean // Flag to indicate this is a position row from pos_map
  is_struct_item?: boolean // Flag to indicate this is a structural position (no status column, not expandable)
  status?: 'done' | 'open'
  calc_status?: StcsCalcItemStatus // Backend calculation status (errors, warnings, etc.)
}

export interface EditAPIOptions {
  successCallback?: (response: MultiGenericPayloadResponse) => void
  errorCallback?: (error: Error) => void
  cancelPrevious?: boolean // If true, will cancel previous pending request
}

function debugLog(message: string, data?: unknown) {
  consola.log(`DEVELOP:edit_api: ${message}`, data)
}

function debugError(message: string, error?: unknown) {
  consola.error(`DEVELOP:edit_api: ${message}`, error)
}

// Store the current active subscription for cancellation
let activeSubscription: Subscription | null = null

/**
 * Cancel any active API request
 * Useful for cleanup scenarios (e.g., component unmount, navigation)
 */
export function cancelActiveEditRequest(): void {
  if (activeSubscription && !activeSubscription.closed) {
    debugLog('🚫 [Unified Edit API] Manually cancelling active request')
    activeSubscription.unsubscribe()
    activeSubscription = null
  }
}

/**
 * Check if there's an active API request in progress
 */
export function hasActiveEditRequest(): boolean {
  return activeSubscription !== null && !activeSubscription.closed
}

export async function callEditAPI(options: EditAPIOptions): Promise<void> {
  // Cancel previous request if requested and one exists
  if (options.cancelPrevious && activeSubscription && !activeSubscription.closed) {
    debugLog('🚫 [Unified Edit API] Cancelling previous pending request')
    activeSubscription.unsubscribe()
    activeSubscription = null
  }
  try {
    // 3. Prepare data structures for each method
    const editPayload = prepareUnifiedEditPayload()
    debugLog('Payload:', editPayload)

    // 4. Make the API call
    const response = await executeEditRequest(editPayload, options)

    // 5. Execute success callback
    if (options.successCallback && response) {
      options.successCallback(response)
    }
  } catch (error) {
    debugError('❌ [Unified Edit API] Error occurred:', error)
    if (options.errorCallback) {
      options.errorCallback(error as Error)
    }
    throw error
  }
}

/**
 * Unified function to identify valid custom rows that should receive backend updates
 * Works for both runtime-added rows and page-reload rows
 */
export function isValidPlanningCustomRow(row: PlanningRowData): boolean {
  // Case 1: Rows loaded from backend pos_map (after page reload)
  if (row?.is_position_row === true) {
    return true
  }

  // Case 2: Runtime-added custom rows (before page reload)
  const runtimeCustomClasses = ['custom-row', 'sub-custom-row', 'account-row', 'tag-row']
  if (runtimeCustomClasses.includes(row?.rowClass)) {
    return true
  }

  // Case 3: Rows with explicit planning methods
  if (row?.selectedMethod && row?.selectedMethod !== undefined) {
    return true
  }

  return false
}

export function prepareUnifiedEditPayload(isStatusUpdate = false): CompPlanCfgCalcRequest {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const payload: CompPlanCfgCalcRequest = {
    ref_id_user: ctxStore.ref_user.id.extractId(),
    comp_plan_cfg: {
      id: planState.id,
      version: new Date().toISOString().replace('Z', '+00:00'),
      is_draft: planState.is_draft ?? true,
      meta: {
        ref_id_user: ctxStore.ref_user.id.extractId(),
        ref_id_org: ctxStore.active_org_id,
        ref_id_comp: ctxStore.active_comp_id,
        fpna_begin_date_string: planState.fpna_begin_date_string ?? '',
        fpna_end_date_string: planState.fpna_end_date_string ?? '',
        obj_created_iso_utc: planState.obj_created_iso_utc ?? '',
        obj_updated_iso_utc: planState.obj_updated_iso_utc ?? '',
      },
      seas_map: {},
      pnl_map: {},
      bs_map: {},
      i18n_custom_key: ctxStore.active_org_id,
      i18n_map: {}, // Will be populated with minimal pos_map translations
    },
  }

  // Generate direct entries from method analysis results
  generateDirectEntries(payload, isStatusUpdate)

  // Populate minimal i18n translations for pos_map entries only
  populatePosMapTranslations(payload)

  // Add BS positions to payload
  const retained_earnings = findRowByEndsWith(planState.bsList, 'retained_earnings')
  const profit_loss_carryforward = findRowByEndsWith(planState.bsList, 'profit_loss_carryforward')
  const cash_and_cash_equivalents = findRowByEndsWith(planState.bsList, 'cash_n_cash_equivalents')
  const overrideIds = [retained_earnings, profit_loss_carryforward, cash_and_cash_equivalents]

  if (payload.comp_plan_cfg) {
    payload.comp_plan_cfg.bs_map = {
      ...Object.fromEntries(Object.entries(payload.comp_plan_cfg.bs_map).filter(([_, value]) => Object.keys(value.pos_map || {}).length > 0 || value.is_done === true)),
      ...Object.fromEntries(Object.entries(planningStore.planConfigCache[planState.id]?.stcs_comp_plan_cfg?.bs_map || {}).filter(([key]) => overrideIds.includes(key))),
    }

    payload.comp_plan_cfg.pnl_map = {
      // Filter pnl_map to exclude entries with empty pos_map
      ...Object.fromEntries(Object.entries(payload.comp_plan_cfg.pnl_map).filter(([_, value]) => Object.keys(value.pos_map || {}).length > 0 || value.is_done === true)),
    }
  }

  return payload
}

function createDirectPlanItemCfg(row: PlanningRowData): StcsCompPlanCfgItem {
  return {
    fstmt_id: row.row_id,
    level_1_id: extractLevel1Id(row.row_id),
    level_2_id: extractLevel2Id(row.row_id),
    depends_on: [],
    fstmt_type: row.fstmt_type,
    is_calculated: false, // Will be determined by the backend
    pos_map: {},
    res_map: {},
    run_last: false,
    is_done: cleanString(row?.status ?? '') === 'done',
    // ref_id_ctx_line_source: `Direct entry for ${row.row_id}`,
    is_struct_item: false, // This is handled by the backend only
    plan_item_logic_type: PlanItemLogicEnum.PLAN_ITEM_LOGIC_ENUM_POS_INPUT_ADD,
  }
}

function generateDirectEntries(payload: CompPlanCfgCalcRequest, isStatusUpdate = false): void {
  // Get planning store to access all rows from both PnL and BS
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const allowedFpnaTypes = [StcsFpnaTypeEnum.SFT_ACC_ITEM, StcsFpnaTypeEnum.SFT_LEVEL_3, StcsFpnaTypeEnum.SFT_LEVEL_2]

  // Get ALL rows from both PnL and BS from planning store (not just current grid)
  const allPnlRowsFromStore = planState.pnlList?.filter((row) => allowedFpnaTypes.includes(row.fpna_type))
  const allBsRowsFromStore = planState.bsList?.filter((row) => allowedFpnaTypes.includes(row.fpna_type))

  // Process PnL and BS rows using planning store data
  processPnlEntriesFromStore(payload, allPnlRowsFromStore, isStatusUpdate)
  processBsEntriesFromStore(payload, allBsRowsFromStore, isStatusUpdate)
}

function processPnlEntriesFromStore(payload: CompPlanCfgCalcRequest, allPnlRowsFromStore: PlanningRowData[], isStatusUpdate = false): void {
  // Group rows by their FSD parent using path_level logic instead of gridApi
  const pnlFsdGroups = groupRowsByFsdParentFromStore(allPnlRowsFromStore)
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const validPnlGroups = Object.entries(pnlFsdGroups).filter(([fsdRowId, groupedRows]) => {
    const fsdRow = planState.pnlList?.find((r) => r.row_id === fsdRowId)
    const hasStatusDone = cleanString(fsdRow?.status ?? '') === 'done'
    const hasPosMapEntries = groupedRows.customRows.length > 0

    return hasStatusDone || hasPosMapEntries
  })

  for (const [fsdRowId, groupedRows] of validPnlGroups) {
    if (payload.comp_plan_cfg) {
      const planItemCfg = createPlanItemCfgWithCustomRows(groupedRows, payload, isStatusUpdate)
      payload.comp_plan_cfg.pnl_map[fsdRowId] = planItemCfg
    }
  }
}

function processBsEntriesFromStore(payload: CompPlanCfgCalcRequest, allBsRowsFromStore: PlanningRowData[], isStatusUpdate = false): void {
  // Group rows by their FSD parent using path_level logic instead of gridApi
  const bsFsdGroups = groupRowsByFsdParentFromStore(allBsRowsFromStore)
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  // Filter to only include FSD rows that have at least one valid pos_map entry
  const validBsGroups = Object.entries(bsFsdGroups).filter(([fsdRowId, groupedRows]) => {
    const fsdRow = planState.bsList?.find((r) => r.row_id === fsdRowId)
    const hasStatusDone = cleanString(fsdRow?.status ?? '') === 'done'
    const hasPosMapEntries = groupedRows.customRows.length > 0

    // Pass if status is "done" OR has pos_map entries
    return hasStatusDone || hasPosMapEntries
  })

  for (const [fsdRowId, groupedRows] of validBsGroups) {
    if (payload.comp_plan_cfg) {
      const planItemCfg = createPlanItemCfgWithCustomRows(groupedRows, payload, isStatusUpdate)
      payload.comp_plan_cfg.bs_map[fsdRowId] = planItemCfg
    }
  }
}

function createPlanItemCfgWithCustomRows(groupedRows: { fsdRow: PlanningRowData; customRows: PlanningRowData[] }, payload: CompPlanCfgCalcRequest, isStatusUpdate = false): StcsCompPlanCfgItem {
  const { fsdRow, customRows } = groupedRows
  // Create the FSD entry
  const planItemCfg = createDirectPlanItemCfg(fsdRow)

  // // Define override IDs that should be skipped
  const overrideIds = ['retained_earnings', 'profit_loss_carryforward', 'cash_n_cash_equivalents']
  const shouldSkipRow = overrideIds.some((id) => fsdRow.row_id.endsWith(id))

  // Skip processing custom rows if this FSD row is an override ID
  if (shouldSkipRow) {
    return planItemCfg
  }

  // Add custom rows to pos_map and capture translation mappings
  // Set pos_index based on the order they appear in customRows array
  for (let index = 0; index < customRows.length; index++) {
    const customRow = customRows[index]
    const posMapKey = customRow.row_id
    const posMapEntry = createPosMapEntry(customRow, payload, isStatusUpdate)
    // Set the pos_index to maintain proper ordering
    posMapEntry.pos_index = index + 1

    planItemCfg.pos_map[posMapKey] = posMapEntry
  }

  return planItemCfg
}

/**
 * Check if a row should be excluded from pos_map based on rowClass
 */
function shouldExcludeFromPosMap(rowClass: string | undefined): boolean {
  const excludedRowClasses = ['total-cml-ctx-line-percent-child', 'custom-planning-row', 'sub-custom-row']
  return excludedRowClasses.includes(rowClass || '')
}

/**
 * Group rows by FSD parent using planning store data instead of GridAPI
 * This ensures we get ALL rows regardless of which grid is currently active
 */

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function groupRowsByFsdParentFromStore(allRowsFromStore: PlanningRowData[]): Record<string, { fsdRow: PlanningRowData; customRows: PlanningRowData[] }> {
  const groups: Record<string, { fsdRow: PlanningRowData; customRows: PlanningRowData[] }> = {}

  // First pass: identify FSD rows and create groups
  for (const row of allRowsFromStore) {
    // Determine if this is an FSD row (parent) or custom row (child)
    const hasCtxLine = row.row_id.includes(CTX_LINE_SUFFIX)
    // const hasTimestamp = TIMESTAMP_SUFFIX_PATTERN.test(row.row_id)
    const isFsdRow = !(
      hasCtxLine ||
      row.is_position_row ||
      row.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM ||
      row.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_3 ||
      isValidPlanningCustomRow(row) ||
      ['selected-method-row', 'custom-planning-row'].includes(row.rowClass)
    )

    if (isFsdRow) {
      // This is a top-level FSD row - initialize group
      groups[row.row_id] = {
        fsdRow: row,
        customRows: [],
      }
    }
  }

  // Second pass: find custom rows and assign to their FSD parents
  for (const row of allRowsFromStore) {
    const hasCtxLine = row.row_id.includes(CTX_LINE_SUFFIX)
    const hasTimestamp = TIMESTAMP_SUFFIX_PATTERN.test(row.row_id)
    const isCustomRow = (isValidPlanningCustomRow(row) || hasCtxLine || hasTimestamp || row.is_position_row) && row.selectedMethod // Ensure it has a selectedMethod

    if (isCustomRow) {
      // Find the FSD parent by traversing up the path_level
      const parentFsdRowId = findFsdParentFromPath(row, groups)

      if (parentFsdRowId && groups[parentFsdRowId]) {
        // Check if this row should be excluded from pos_map
        const shouldExclude = shouldExcludeFromPosMap(row.rowClass)

        if (!shouldExclude) {
          groups[parentFsdRowId].customRows.push(row)
        }
      }
    }
  }

  return groups
}

/**
 * Helper function to find FSD parent from a custom row's path_level
 */
function findFsdParentFromPath(customRow: PlanningRowData, fsdGroups: Record<string, { fsdRow: PlanningRowData; customRows: PlanningRowData[] }>): string | null {
  const pathLevel = customRow.path_level || []

  // Walk up the path to find an FSD row that exists in our groups
  for (const pathId of pathLevel) {
    if (fsdGroups[pathId]) {
      return pathId
    }
  }

  return null
}

function createPosMapEntry(row: PlanningRowData, payload: CompPlanCfgCalcRequest, isStatusUpdate = false): PlanPosItem {
  const method = row.selectedMethod

  switch (method) {
    case MethodologyEnum.MANUAL_DEC_INPUT:
      return createManualPosEntry(row, false, isStatusUpdate)
    case MethodologyEnum.MONTHLY_GROWTH_RATE:
      return createMonthlyGrowthPosEntry(row, false, isStatusUpdate)
    case MethodologyEnum.ANNUALLY_GROWTH_RATE:
      return createAnnualGrowthPosEntry(row, payload, isStatusUpdate)
    case MethodologyEnum.FORMULA_BASED:
      return createFormulaBasedPosEntry(row, false, isStatusUpdate)
    case MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE:
      return createManualChangePosEntry(row, isStatusUpdate)
    default:
      // biome-ignore lint/style/useSingleCaseStatement: <explanation>
      debugLog(`⚠️ [Pos Entry] Unknown selectedMethod: ${method}, defaulting to manual`)
      return createManualPosEntry(row)
  }
}

export function createManualPosEntry(row: PlanningRowData, isSubPosition = false, isStatusUpdate = false, parentRow?: PlanningRowData): PlanPosItem {
  const inputMap = createValidInputManualMap(row)
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Get pos_res_plan_map from cache only when isStatusUpdate is true
  let pos_res_plan_map = {}
  if (isStatusUpdate && selectedPlan) {
    const IS_PNL = row.fstmt_type === StcsFstmtEnum.SFSE_PNL
    const mapKey = IS_PNL ? 'pnl_map' : 'bs_map'

    if (isSubPosition && parentRow) {
      // For sub-positions, look in parent's formula_based.sub_pos_map
      const grandParentRowId = parentRow.path_level[parentRow.path_level.length - 2]
      pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[grandParentRowId]?.pos_map?.[parentRow.row_id]?.formula_based?.sub_pos_map?.[row.row_id]?.manual_dec_input?.pos_res_plan_map || {}
    } else {
      // For main positions, look in pos_map
      const parentRowId = row.path_level[row.path_level.length - 2]
      pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[parentRowId]?.pos_map?.[row.row_id]?.manual_dec_input?.pos_res_plan_map || {}
    }
  }

  return {
    pos_logic_type: MethodologyEnum.MANUAL_DEC_INPUT,
    fstmt_type: row.fstmt_type,
    display_value_type: row?.outputType || StcsDisplayValueTypeEnum.SDVTE_CURRENCY,
    manual_dec_input: {
      pos_input_map: inputMap,
      pos_res_plan_map: pos_res_plan_map,
    },
    is_struct_item: false, // Handle by the backend only
    pos_index: 0,
    status: undefined, // Handle by the backend only
  }
}

export function createValidInputManualMap(row: PlanningRowData): Record<string, InputManualMap> {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const { valuesMapPnl, valuesMapBs } = planState

  // Determine statement type and select values map
  const isPnl = row.fstmt_type === StcsFstmtEnum.SFSE_PNL
  const valuesMap = (isPnl ? valuesMapPnl?.[row.row_id]?.values_map : valuesMapBs?.[row.row_id]?.values_map) ?? {}

  // Create validated input map in a single pass
  const validatedInputMap: Record<string, InputManualMap> = {}
  for (const [period, value] of Object.entries(valuesMap)) {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
      continue
    }

    validatedInputMap[period] = {
      manual_number_input: value,
    }
  }

  return validatedInputMap
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function populatePosMapTranslations(payload: CompPlanCfgCalcRequest): void {
  debugLog('🌐 [i18n] Building minimal pos_map translations using organization locale with user language...')

  if (!payload.comp_plan_cfg) {
    return
  }

  // Get i18n data from context store
  const ctxStore = useCtxStore()
  const activeOrgId = ctxStore.active_org_id
  const ctxI18n = ctxStore.ref_fsd_map[activeOrgId]?.i18n_map[activeOrgId]?.label_map || {}
  const ctxI18nEn = ctxStore.ref_fsd_map[activeOrgId]?.i18n_map.en?.label_map || {}
  const ctxI18nDe = ctxStore.ref_fsd_map[activeOrgId]?.i18n_map.de?.label_map || {}

  // Get planning store to access all rows from both PnL and BS
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const plani18Map = planningStore.planConfigCache[planState.id]?.stcs_comp_plan_cfg?.i18n_map[activeOrgId].label_map
  const allValidCustomRows = [...planState.pnlList, ...planState.bsList].filter(isValidPlanningCustomRow)

  // Initialize minimal i18n_map structure with organization locale
  const I18Map: Record<string, { label_map: Record<string, string> }> = {
    [activeOrgId]: {
      label_map: {
        stcs_comp_plan_display_name: planState?.display_name ?? '',
      },
    },
  }

  for (const row of allValidCustomRows) {
    const rowId = row.row_id
    const levelId = row.level_id
    const isAccRow = row.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM
    const isLevel3Row = row.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_3

    if (isAccRow) {
      const translation = plani18Map?.[levelId] ?? plani18Map?.[rowId]
      const prefix = levelId

      if (translation) {
        I18Map[activeOrgId].label_map[rowId] = translation
      } else {
        I18Map[activeOrgId].label_map[rowId] = `${prefix} ${ctxI18n[rowId] ?? ctxI18n[levelId]}`
      }
    } else if (isLevel3Row) {
      I18Map[activeOrgId].label_map[rowId] = plani18Map?.[rowId] ?? plani18Map?.[levelId] ?? ctxI18nEn[levelId] ?? ctxI18nDe[levelId]
    } else {
      I18Map[activeOrgId].label_map[rowId] = plani18Map?.[rowId] ?? plani18Map?.[levelId] ?? ctxI18n[rowId] ?? ctxI18n[levelId] ?? 'n/v123'
    }
  }

  // Add custom seasonality logic names from planConfigCache
  if (plani18Map) {
    Object.keys(plani18Map).forEach((key) => {
      if (key.startsWith('custom_')) {
        I18Map[activeOrgId].label_map[key] = plani18Map[key]
      }
    })
  }

  payload.comp_plan_cfg.i18n_map = I18Map
}

async function executeEditRequest(payload: CompPlanCfgCalcRequest, _options: EditAPIOptions): Promise<MultiGenericPayloadResponse | null> {
  return new Promise((resolve, reject) => {
    // Create subscription to the API call
    activeSubscription = cmwCompPlanCfgCalcRequest(payload).subscribe({
      next: (response) => {
        debugLog('✅ [API Success] Request completed successfully')
        activeSubscription = null
        resolve(response)
      },
      error: (error) => {
        debugError('❌ [API Error] cmwCompPlanCfgCalcRequest failed:', error)
        activeSubscription = null
        reject(new Error(`API call failed: ${error}`))
      },
      complete: () => {
        debugLog('🏁 [API Complete] Observable completed')
      },
    })
  })
}

export function hasFormulaBasedPositions(): boolean {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  return planState.pnlList.some((row) => row.selectedMethod === MethodologyEnum.FORMULA_BASED) || planState.bsList.some((row) => row.selectedMethod === MethodologyEnum.FORMULA_BASED)
}
